import React, { FC, useCallback, useMemo } from 'react'
import { useFieldArray, useController, useFormContext } from 'react-hook-form'
import { Icon, AsyncSelect } from '@wmgtech/legato'
import compact from 'lodash/fp/compact'

import SearchItem from 'components/Search/SearchItem/SearchItem'
import { FormRadio, FormWhen } from 'components/Form'

import { AssignedProjectsList } from '..'
import { useSearchProject, useZone } from '../../serviceHooks/'

import { IUserForm } from '../../types'
import './AssignedProjects.scss'
import { Campaign } from 'shared/__gql-types'

const AssignedProjects: FC = () => {
  const { append, remove, fields } = useFieldArray<IUserForm, 'assigned_campaigns'>({ name: 'assigned_campaigns' })

  const { fieldState: { error }} = useController<IUserForm>({ name: 'assigned_campaigns' })

  const { getValues, watch, control } = useFormContext<IUserForm>()

  const { searchCampaign, searchResult, isLoading } = useSearchProject()
  const { byId: zoneById } = useZone()

  const handleSearch = useCallback(
    (term: string) => {
      const [label_id, space_id] = getValues(['label_id', 'space_id'])
      searchCampaign(term, { label_id, space_id })
    },
    [getValues, searchCampaign],
  )

  const fieldsList = useMemo(() => fields.map(({ projectId }) => projectId), [fields])

  const setAssignedProject = useCallback(
    (project: Campaign) => {
      if (typeof project !== 'undefined' && !fieldsList.includes(project?.id || '')) {
        append({
          projectId: project.id || '',
          artistName: project.artist?.name || '',
          projectName: project?.name || '',
          spotifyId: project.artist?.spotify_id || '',
          facebookId: project.artist?.facebook_id || ''
        })
      }
    },
    [append, fieldsList],
  )

  const createTitle = (data?: Campaign) => compact([data?.artist?.name, data?.name]).join(' | ')

  const validationMessage = useCallback(
    () => {
      if (error?.message) return (
        <div className="assigned-projects__error-message">
          <Icon name="triangle-exclamation" prefix="fas" />
          {error.message}
        </div>
      )
      return ''
    },
    [error],
  )

  return (
    <div className="assigned-projects">
      <FormRadio
        label="Projects"
        fieldName="all_campaign"
        control={control}
        grouping="vertical"
        list={[
          {
            label: 'All Storybox Projects',
            value: true,
          },
          {
            label: 'Select Projects',
            value: false,
          }
        ]}
      />
      <FormWhen fieldName="all_campaign" is={value => false === value} control={control}>
        <>
          <AsyncSelect
            name="project"
            isClearable
            placeholder="Project Title"
            isLoading={isLoading}
            getKey={(data) => data?.id || ''}
            validation={{ pending: isLoading }}
            getElement={(data) =>
              <SearchItem
                title={data?.artist?.name || ''}
                subtitle={data?.name || ''}
                spotifyId={data?.artist?.spotify_id || ''}
                facebookId={data?.artist?.facebook_id || ''}
                isSpaceRole={typeof watch('space_id') === 'string'}
                zone={data?.label_id ? zoneById[data?.label_id] : undefined}
              />
            }
            getTitle={createTitle}
            list={searchResult}
            disabled={compact(watch(['label_id', 'space_id'])).length === 0}
            onSearch={handleSearch}
            onSelect={setAssignedProject}
          />
          {
            fields.length ? (
              <AssignedProjectsList
                assignedProjects={fields}
                unassignProject={remove}
                emptyText={validationMessage}
              />
            ) : null
          }
        </>
      </FormWhen>
    </div>
  )
}

export default AssignedProjects
